/**
 * Generic Integration Configuration
 * 
 * This file defines all integration types and their specific configurations.
 * Each integration can support multiple source types (CSV, API, WebCrawl, etc.)
 * 
 * Translation keys are used for all user-facing text.
 * The actual translations are in i18n/locales/[lang].json under integrations.types.*
 */

// ---------------------------------------------------------------------------
// Action Templates — used by CSV sources to define generic column-mapped actions
// ---------------------------------------------------------------------------

export interface ActionTemplateSlot {
  id: string;        // param name in the schema (e.g. "oe_number", "price")
  label: string;     // human-readable label shown in the builder
  required: boolean;
  description?: string;
}

export interface ActionTemplate {
  id: string;                        // e.g. 'lookup_supplier_price' | 'general_lookup'
  name: string;                      // default action name (editable by user)
  description: string;
  inputSlots: ActionTemplateSlot[];  // default lookup key columns
  inputSlotsEditable: boolean;       // true = user can add/remove input slots freely
  outputSlots: ActionTemplateSlot[]; // columns returned as results
  outputSlotsEditable: boolean;      // true = user can add/remove output slots freely
}

export interface IntegrationSourceType {
  id: string;
  nameKey: string; // Translation key for the name
  icon: string; // Icon name from lucide-react
  descriptionKey: string; // Translation key for the description
  color: 'blue' | 'green' | 'purple' | 'orange' | 'red';
  fields: IntegrationField[];
  supportsActions?: boolean; // For webcrawl-like action definitions
  /**
   * Action templates for CSV sources — drives the CsvActionBuilder UI.
   * When present, the static _column fields are replaced by action-based column mapping.
   */
  actionTemplates?: ActionTemplate[];
  /**
   * Pre-seeded actions that are automatically created when this source type is saved.
   * The user does not need to configure these manually — they are injected into the
   * create/update payload by GenericIntegrationSetupPage before submission.
   */
  defaultActions?: WebCrawlAction[];
  helpContent: {
    titleKey: string; // Translation key for help title
    itemsKeys: string[]; // Translation keys for help items
    examples?: { titleKey: string; descriptionKey: string }[];
  };
}

export interface IntegrationField {
  id: string;
  labelKey: string; // Translation key for the label
  type: 'text' | 'password' | 'number' | 'textarea' | 'select' | 'file' | 'json' | 'checkbox';
  required: boolean;
  placeholderKey?: string; // Translation key for the placeholder
  descriptionKey?: string; // Translation key for the description
  options?: { value: string; label: string }[]; // For select fields
  validation?: {
    pattern?: string;
    min?: number;
    max?: number;
    fileTypes?: string[];
    maxSize?: number; // in MB
  };
}

export interface WebCrawlAction {
  id: string;
  name: string;
  description: string;
  inputParameters: {
    name: string;
    type: 'string' | 'number';
    required: boolean;
    description: string;
  }[];
  outputFormat: {
    type: 'json' | 'text';
    schema?: string;
  };
  instructions: string;
  /** JSON Schema (draft-07) for inputs — preferred over inputParameters when present */
  inputSchema?: {
    type: 'object';
    properties: Record<string, { type: string; description?: string; enum?: any[] }>;
    required?: string[];
    [key: string]: any;
  };
  /** JSON Schema (draft-07) for outputs — preferred over outputFormat when present */
  outputSchema?: {
    type: 'object';
    properties: Record<string, { type: string; description?: string; enum?: any[] }>;
    required?: string[];
    [key: string]: any;
  };
  /**
   * Column mapping for CSV-based actions (set by CsvActionBuilder).
   * Maps slot ids → actual CSV column names.
   *   input:  { oe_number: "OE_NUM" }
   *   output: { price: "UNIT_PRICE", currency: "CCY" }
   */
  column_mapping?: {
    input: Record<string, string>;
    output: Record<string, string>;
    /** MCP sources: the server tool this action calls */
    tool?: string;
  };
  /** Template id this action was created from (e.g. 'lookup_supplier_price') */
  template_id?: string;
}

export interface IntegrationSource {
  id: string;
  name: string;
  type: string; // sourceType id
  status: 'active' | 'inactive' | 'syncing' | 'error' | 'crawling';
  lastSync?: string;
  itemCount?: number;
  config?: Record<string, any>;
  actions?: WebCrawlAction[];
  shared_organization_ids?: number[];
}

export interface IntegrationConfig {
  id: string;
  nameKey: string; // Translation key for the name
  descriptionKey: string; // Translation key for the description
  icon: string; // Icon name from lucide-react
  category: 'data-sources' | 'operations' | 'business-systems';
  sourceTypes: IntegrationSourceType[];
  additionalSettings?: IntegrationField[]; // Global settings for this integration
}

// Define all integrations
export const INTEGRATIONS: Record<string, IntegrationConfig> = {
  inventory: {
    id: 'inventory',
    nameKey: 'integrations.types.inventory.name',
    descriptionKey: 'integrations.types.inventory.description',
    icon: 'Package',
    category: 'data-sources',
    sourceTypes: [
      {
        id: 'api',
        nameKey: 'integrations.types.inventory.sourceTypes.api.name',
        icon: 'Plug',
        descriptionKey: 'integrations.types.inventory.sourceTypes.api.description',
        color: 'blue',
        supportsActions: true,
        fields: [
          {
            id: 'baseUrl',
            labelKey: 'integrations.types.inventory.sourceTypes.api.fields.baseUrl.label',
            type: 'text',
            required: true,
            placeholderKey: 'integrations.types.inventory.sourceTypes.api.fields.baseUrl.placeholder',
            descriptionKey: 'integrations.types.inventory.sourceTypes.api.fields.baseUrl.description'
          },
          {
            id: 'authType',
            labelKey: 'integrations.types.inventory.sourceTypes.api.fields.authType.label',
            type: 'select',
            required: true,
            options: [
              { value: 'api_key', label: 'API Key' },
              { value: 'bearer', label: 'Bearer Token' },
              { value: 'basic', label: 'Basic Auth' },
              { value: 'none', label: 'No Auth' }
            ]
          },
          {
            id: 'apiKey',
            labelKey: 'integrations.types.inventory.sourceTypes.api.fields.apiKey.label',
            type: 'password',
            required: false,
            placeholderKey: 'integrations.types.inventory.sourceTypes.api.fields.apiKey.placeholder',
            descriptionKey: 'integrations.types.inventory.sourceTypes.api.fields.apiKey.description'
          },
          {
            id: 'customHeaders',
            labelKey: 'integrations.types.inventory.sourceTypes.api.fields.customHeaders.label',
            type: 'json',
            required: false,
            placeholderKey: 'integrations.types.inventory.sourceTypes.api.fields.customHeaders.placeholder',
            descriptionKey: 'integrations.types.inventory.sourceTypes.api.fields.customHeaders.description'
          }
        ],
        helpContent: {
          titleKey: 'integrations.types.inventory.sourceTypes.api.helpContent.title',
          itemsKeys: [
            'integrations.types.inventory.sourceTypes.api.helpContent.items.0',
            'integrations.types.inventory.sourceTypes.api.helpContent.items.1',
            'integrations.types.inventory.sourceTypes.api.helpContent.items.2',
            'integrations.types.inventory.sourceTypes.api.helpContent.items.3'
          ],
          examples: [
            {
              titleKey: 'integrations.types.inventory.sourceTypes.api.helpContent.examples.checkStock.title',
              descriptionKey: 'integrations.types.inventory.sourceTypes.api.helpContent.examples.checkStock.description'
            },
            {
              titleKey: 'integrations.types.inventory.sourceTypes.api.helpContent.examples.getPartDetails.title',
              descriptionKey: 'integrations.types.inventory.sourceTypes.api.helpContent.examples.getPartDetails.description'
            }
          ]
        }
      },
      {
        id: 'webcrawl',
        nameKey: 'integrations.types.inventory.sourceTypes.webcrawl.name',
        icon: 'Sparkles',
        descriptionKey: 'integrations.types.inventory.sourceTypes.webcrawl.description',
        color: 'purple',
        supportsActions: true,
        fields: [
          {
            id: 'websiteUrl',
            labelKey: 'integrations.types.inventory.sourceTypes.webcrawl.fields.websiteUrl.label',
            type: 'text',
            required: true,
            placeholderKey: 'integrations.types.inventory.sourceTypes.webcrawl.fields.websiteUrl.placeholder',
            descriptionKey: 'integrations.types.inventory.sourceTypes.webcrawl.fields.websiteUrl.description'
          },
          {
            id: 'username',
            labelKey: 'integrations.types.inventory.sourceTypes.webcrawl.fields.username.label',
            type: 'text',
            required: false,
            placeholderKey: 'integrations.types.inventory.sourceTypes.webcrawl.fields.username.placeholder',
            descriptionKey: 'integrations.types.inventory.sourceTypes.webcrawl.fields.username.description'
          },
          {
            id: 'password',
            labelKey: 'integrations.types.inventory.sourceTypes.webcrawl.fields.password.label',
            type: 'password',
            required: false,
            placeholderKey: 'integrations.types.inventory.sourceTypes.webcrawl.fields.password.placeholder',
            descriptionKey: 'integrations.types.inventory.sourceTypes.webcrawl.fields.password.description'
          },
          {
            id: 'crawlInstructions',
            labelKey: 'integrations.types.inventory.sourceTypes.webcrawl.fields.crawlInstructions.label',
            type: 'textarea',
            required: true,
            placeholderKey: 'integrations.types.inventory.sourceTypes.webcrawl.fields.crawlInstructions.placeholder',
            descriptionKey: 'integrations.types.inventory.sourceTypes.webcrawl.fields.crawlInstructions.description'
          }
        ],
        helpContent: {
          titleKey: 'integrations.types.inventory.sourceTypes.webcrawl.helpContent.title',
          itemsKeys: [
            'integrations.types.inventory.sourceTypes.webcrawl.helpContent.items.0',
            'integrations.types.inventory.sourceTypes.webcrawl.helpContent.items.1',
            'integrations.types.inventory.sourceTypes.webcrawl.helpContent.items.2',
            'integrations.types.inventory.sourceTypes.webcrawl.helpContent.items.3',
            'integrations.types.inventory.sourceTypes.webcrawl.helpContent.items.4'
          ],
          examples: [
            {
              titleKey: 'integrations.types.inventory.sourceTypes.webcrawl.helpContent.examples.checkPartExists.title',
              descriptionKey: 'integrations.types.inventory.sourceTypes.webcrawl.helpContent.examples.checkPartExists.description'
            },
            {
              titleKey: 'integrations.types.inventory.sourceTypes.webcrawl.helpContent.examples.getPartQuantity.title',
              descriptionKey: 'integrations.types.inventory.sourceTypes.webcrawl.helpContent.examples.getPartQuantity.description'
            }
          ]
        }
      }
    ]
  },

  quotes: {
    id: 'quotes',
    nameKey: 'integrations.types.quotes.name',
    descriptionKey: 'integrations.types.quotes.description',
    icon: 'FileText',
    category: 'operations',
    sourceTypes: [
      {
        id: 'api',
        nameKey: 'integrations.types.quotes.sourceTypes.api.name',
        icon: 'Plug',
        descriptionKey: 'integrations.types.quotes.sourceTypes.api.description',
        color: 'blue',
        supportsActions: true,
        fields: [
          {
            id: 'baseUrl',
            labelKey: 'integrations.types.quotes.sourceTypes.api.fields.baseUrl.label',
            type: 'text',
            required: true,
            placeholderKey: 'integrations.types.quotes.sourceTypes.api.fields.baseUrl.placeholder',
            descriptionKey: 'integrations.types.quotes.sourceTypes.api.fields.baseUrl.description'
          },
          {
            id: 'authType',
            labelKey: 'integrations.types.quotes.sourceTypes.api.fields.authType.label',
            type: 'select',
            required: true,
            options: [
              { value: 'api_key', label: 'API Key' },
              { value: 'bearer', label: 'Bearer Token' },
              { value: 'basic', label: 'Basic Auth' },
              { value: 'none', label: 'No Auth' }
            ]
          },
          {
            id: 'apiKey',
            labelKey: 'integrations.types.quotes.sourceTypes.api.fields.apiKey.label',
            type: 'password',
            required: false,
            placeholderKey: 'integrations.types.quotes.sourceTypes.api.fields.apiKey.placeholder',
            descriptionKey: 'integrations.types.quotes.sourceTypes.api.fields.apiKey.description'
          },
          {
            id: 'customHeaders',
            labelKey: 'integrations.types.quotes.sourceTypes.api.fields.customHeaders.label',
            type: 'json',
            required: false,
            placeholderKey: 'integrations.types.quotes.sourceTypes.api.fields.customHeaders.placeholder',
            descriptionKey: 'integrations.types.quotes.sourceTypes.api.fields.customHeaders.description'
          }
        ],
        helpContent: {
          titleKey: 'integrations.types.quotes.sourceTypes.api.helpContent.title',
          itemsKeys: [
            'integrations.types.quotes.sourceTypes.api.helpContent.items.0',
            'integrations.types.quotes.sourceTypes.api.helpContent.items.1',
            'integrations.types.quotes.sourceTypes.api.helpContent.items.2',
            'integrations.types.quotes.sourceTypes.api.helpContent.items.3'
          ],
          examples: [
            {
              titleKey: 'integrations.types.quotes.sourceTypes.api.helpContent.examples.createQuote.title',
              descriptionKey: 'integrations.types.quotes.sourceTypes.api.helpContent.examples.createQuote.description'
            },
            {
              titleKey: 'integrations.types.quotes.sourceTypes.api.helpContent.examples.getQuote.title',
              descriptionKey: 'integrations.types.quotes.sourceTypes.api.helpContent.examples.getQuote.description'
            }
          ]
        }
      },
      {
        id: 'webcrawl',
        nameKey: 'integrations.types.quotes.sourceTypes.webcrawl.name',
        icon: 'Sparkles',
        descriptionKey: 'integrations.types.quotes.sourceTypes.webcrawl.description',
        color: 'purple',
        supportsActions: true,
        fields: [
          {
            id: 'websiteUrl',
            labelKey: 'integrations.types.quotes.sourceTypes.webcrawl.fields.websiteUrl.label',
            type: 'text',
            required: true,
            placeholderKey: 'integrations.types.quotes.sourceTypes.webcrawl.fields.websiteUrl.placeholder',
            descriptionKey: 'integrations.types.quotes.sourceTypes.webcrawl.fields.websiteUrl.description'
          },
          {
            id: 'username',
            labelKey: 'integrations.types.quotes.sourceTypes.webcrawl.fields.username.label',
            type: 'text',
            required: true,
            placeholderKey: 'integrations.types.quotes.sourceTypes.webcrawl.fields.username.placeholder',
            descriptionKey: 'integrations.types.quotes.sourceTypes.webcrawl.fields.username.description'
          },
          {
            id: 'password',
            labelKey: 'integrations.types.quotes.sourceTypes.webcrawl.fields.password.label',
            type: 'password',
            required: true,
            placeholderKey: 'integrations.types.quotes.sourceTypes.webcrawl.fields.password.placeholder',
            descriptionKey: 'integrations.types.quotes.sourceTypes.webcrawl.fields.password.description'
          },
          {
            id: 'crawlInstructions',
            labelKey: 'integrations.types.quotes.sourceTypes.webcrawl.fields.crawlInstructions.label',
            type: 'textarea',
            required: true,
            placeholderKey: 'integrations.types.quotes.sourceTypes.webcrawl.fields.crawlInstructions.placeholder',
            descriptionKey: 'integrations.types.quotes.sourceTypes.webcrawl.fields.crawlInstructions.description'
          }
        ],
        helpContent: {
          titleKey: 'integrations.types.quotes.sourceTypes.webcrawl.helpContent.title',
          itemsKeys: [
            'integrations.types.quotes.sourceTypes.webcrawl.helpContent.items.0',
            'integrations.types.quotes.sourceTypes.webcrawl.helpContent.items.1',
            'integrations.types.quotes.sourceTypes.webcrawl.helpContent.items.2',
            'integrations.types.quotes.sourceTypes.webcrawl.helpContent.items.3'
          ],
          examples: [
            {
              titleKey: 'integrations.types.quotes.sourceTypes.webcrawl.helpContent.examples.createQuote.title',
              descriptionKey: 'integrations.types.quotes.sourceTypes.webcrawl.helpContent.examples.createQuote.description'
            },
            {
              titleKey: 'integrations.types.quotes.sourceTypes.webcrawl.helpContent.examples.lookupCustomer.title',
              descriptionKey: 'integrations.types.quotes.sourceTypes.webcrawl.helpContent.examples.lookupCustomer.description'
            }
          ]
        }
      }
    ]
  },

  crm: {
    id: 'crm',
    nameKey: 'integrations.types.crm.name',
    descriptionKey: 'integrations.types.crm.description',
    icon: 'Users',
    category: 'operations',
    sourceTypes: [
      {
        id: 'api',
        nameKey: 'integrations.types.crm.sourceTypes.api.name',
        icon: 'Plug',
        descriptionKey: 'integrations.types.crm.sourceTypes.api.description',
        color: 'blue',
        supportsActions: true,
        fields: [
          {
            id: 'baseUrl',
            labelKey: 'integrations.types.crm.sourceTypes.api.fields.baseUrl.label',
            type: 'text',
            required: true,
            placeholderKey: 'integrations.types.crm.sourceTypes.api.fields.baseUrl.placeholder',
            descriptionKey: 'integrations.types.crm.sourceTypes.api.fields.baseUrl.description'
          },
          {
            id: 'authType',
            labelKey: 'integrations.types.crm.sourceTypes.api.fields.authType.label',
            type: 'select',
            required: true,
            options: [
              { value: 'api_key', label: 'API Key' },
              { value: 'bearer', label: 'Bearer Token' },
              { value: 'basic', label: 'Basic Auth' },
              { value: 'none', label: 'No Auth' }
            ]
          },
          {
            id: 'apiKey',
            labelKey: 'integrations.types.crm.sourceTypes.api.fields.apiKey.label',
            type: 'password',
            required: false,
            placeholderKey: 'integrations.types.crm.sourceTypes.api.fields.apiKey.placeholder',
            descriptionKey: 'integrations.types.crm.sourceTypes.api.fields.apiKey.description'
          },
          {
            id: 'customHeaders',
            labelKey: 'integrations.types.crm.sourceTypes.api.fields.customHeaders.label',
            type: 'json',
            required: false,
            placeholderKey: 'integrations.types.crm.sourceTypes.api.fields.customHeaders.placeholder',
            descriptionKey: 'integrations.types.crm.sourceTypes.api.fields.customHeaders.description'
          }
        ],
        helpContent: {
          titleKey: 'integrations.types.crm.sourceTypes.api.helpContent.title',
          itemsKeys: [
            'integrations.types.crm.sourceTypes.api.helpContent.items.0',
            'integrations.types.crm.sourceTypes.api.helpContent.items.1',
            'integrations.types.crm.sourceTypes.api.helpContent.items.2',
            'integrations.types.crm.sourceTypes.api.helpContent.items.3'
          ],
          examples: [
            {
              titleKey: 'integrations.types.crm.sourceTypes.api.helpContent.examples.findCustomer.title',
              descriptionKey: 'integrations.types.crm.sourceTypes.api.helpContent.examples.findCustomer.description'
            },
            {
              titleKey: 'integrations.types.crm.sourceTypes.api.helpContent.examples.updateCustomer.title',
              descriptionKey: 'integrations.types.crm.sourceTypes.api.helpContent.examples.updateCustomer.description'
            }
          ]
        }
      },
      {
        id: 'webcrawl',
        nameKey: 'integrations.types.crm.sourceTypes.webcrawl.name',
        icon: 'Sparkles',
        descriptionKey: 'integrations.types.crm.sourceTypes.webcrawl.description',
        color: 'purple',
        supportsActions: true,
        fields: [
          {
            id: 'websiteUrl',
            labelKey: 'integrations.types.crm.sourceTypes.webcrawl.fields.websiteUrl.label',
            type: 'text',
            required: true,
            placeholderKey: 'integrations.types.crm.sourceTypes.webcrawl.fields.websiteUrl.placeholder',
            descriptionKey: 'integrations.types.crm.sourceTypes.webcrawl.fields.websiteUrl.description'
          },
          {
            id: 'username',
            labelKey: 'integrations.types.crm.sourceTypes.webcrawl.fields.username.label',
            type: 'text',
            required: true,
            placeholderKey: 'integrations.types.crm.sourceTypes.webcrawl.fields.username.placeholder',
            descriptionKey: 'integrations.types.crm.sourceTypes.webcrawl.fields.username.description'
          },
          {
            id: 'password',
            labelKey: 'integrations.types.crm.sourceTypes.webcrawl.fields.password.label',
            type: 'password',
            required: true,
            placeholderKey: 'integrations.types.crm.sourceTypes.webcrawl.fields.password.placeholder',
            descriptionKey: 'integrations.types.crm.sourceTypes.webcrawl.fields.password.description'
          },
          {
            id: 'crawlInstructions',
            labelKey: 'integrations.types.crm.sourceTypes.webcrawl.fields.crawlInstructions.label',
            type: 'textarea',
            required: true,
            placeholderKey: 'integrations.types.crm.sourceTypes.webcrawl.fields.crawlInstructions.placeholder',
            descriptionKey: 'integrations.types.crm.sourceTypes.webcrawl.fields.crawlInstructions.description'
          }
        ],
        helpContent: {
          titleKey: 'integrations.types.crm.sourceTypes.webcrawl.helpContent.title',
          itemsKeys: [
            'integrations.types.crm.sourceTypes.webcrawl.helpContent.items.0',
            'integrations.types.crm.sourceTypes.webcrawl.helpContent.items.1',
            'integrations.types.crm.sourceTypes.webcrawl.helpContent.items.2',
            'integrations.types.crm.sourceTypes.webcrawl.helpContent.items.3'
          ],
          examples: [
            {
              titleKey: 'integrations.types.crm.sourceTypes.webcrawl.helpContent.examples.findCustomer.title',
              descriptionKey: 'integrations.types.crm.sourceTypes.webcrawl.helpContent.examples.findCustomer.description'
            },
            {
              titleKey: 'integrations.types.crm.sourceTypes.webcrawl.helpContent.examples.updateCustomer.title',
              descriptionKey: 'integrations.types.crm.sourceTypes.webcrawl.helpContent.examples.updateCustomer.description'
            }
          ]
        }
      }
    ]
  },

  shipping: {
    id: 'shipping',
    nameKey: 'integrations.types.shipping.name',
    descriptionKey: 'integrations.types.shipping.description',
    icon: 'Truck',
    category: 'business-systems',
    sourceTypes: [
      {
        id: 'api',
        nameKey: 'integrations.types.shipping.sourceTypes.api.name',
        icon: 'Plug',
        descriptionKey: 'integrations.types.shipping.sourceTypes.api.description',
        color: 'blue',
        supportsActions: true,
        fields: [
          {
            id: 'baseUrl',
            labelKey: 'integrations.types.shipping.sourceTypes.api.fields.baseUrl.label',
            type: 'text',
            required: true,
            placeholderKey: 'integrations.types.shipping.sourceTypes.api.fields.baseUrl.placeholder',
            descriptionKey: 'integrations.types.shipping.sourceTypes.api.fields.baseUrl.description'
          },
          {
            id: 'authType',
            labelKey: 'integrations.types.shipping.sourceTypes.api.fields.authType.label',
            type: 'select',
            required: true,
            options: [
              { value: 'api_key', label: 'API Key' },
              { value: 'bearer', label: 'Bearer Token' },
              { value: 'basic', label: 'Basic Auth' },
              { value: 'none', label: 'No Auth' }
            ]
          },
          {
            id: 'apiKey',
            labelKey: 'integrations.types.shipping.sourceTypes.api.fields.apiKey.label',
            type: 'password',
            required: false,
            placeholderKey: 'integrations.types.shipping.sourceTypes.api.fields.apiKey.placeholder',
            descriptionKey: 'integrations.types.shipping.sourceTypes.api.fields.apiKey.description'
          },
          {
            id: 'customHeaders',
            labelKey: 'integrations.types.shipping.sourceTypes.api.fields.customHeaders.label',
            type: 'json',
            required: false,
            placeholderKey: 'integrations.types.shipping.sourceTypes.api.fields.customHeaders.placeholder',
            descriptionKey: 'integrations.types.shipping.sourceTypes.api.fields.customHeaders.description'
          }
        ],
        helpContent: {
          titleKey: 'integrations.types.shipping.sourceTypes.api.helpContent.title',
          itemsKeys: [
            'integrations.types.shipping.sourceTypes.api.helpContent.items.0',
            'integrations.types.shipping.sourceTypes.api.helpContent.items.1',
            'integrations.types.shipping.sourceTypes.api.helpContent.items.2',
            'integrations.types.shipping.sourceTypes.api.helpContent.items.3'
          ],
          examples: [
            {
              titleKey: 'integrations.types.shipping.sourceTypes.api.helpContent.examples.trackPackage.title',
              descriptionKey: 'integrations.types.shipping.sourceTypes.api.helpContent.examples.trackPackage.description'
            },
            {
              titleKey: 'integrations.types.shipping.sourceTypes.api.helpContent.examples.createShipment.title',
              descriptionKey: 'integrations.types.shipping.sourceTypes.api.helpContent.examples.createShipment.description'
            }
          ]
        }
      },
      {
        id: 'webcrawl',
        nameKey: 'integrations.types.shipping.sourceTypes.webcrawl.name',
        icon: 'Sparkles',
        descriptionKey: 'integrations.types.shipping.sourceTypes.webcrawl.description',
        color: 'purple',
        supportsActions: true,
        fields: [
          {
            id: 'websiteUrl',
            labelKey: 'integrations.types.shipping.sourceTypes.webcrawl.fields.websiteUrl.label',
            type: 'text',
            required: true,
            placeholderKey: 'integrations.types.shipping.sourceTypes.webcrawl.fields.websiteUrl.placeholder',
            descriptionKey: 'integrations.types.shipping.sourceTypes.webcrawl.fields.websiteUrl.description'
          },
          {
            id: 'username',
            labelKey: 'integrations.types.shipping.sourceTypes.webcrawl.fields.username.label',
            type: 'text',
            required: true,
            placeholderKey: 'integrations.types.shipping.sourceTypes.webcrawl.fields.username.placeholder',
            descriptionKey: 'integrations.types.shipping.sourceTypes.webcrawl.fields.username.description'
          },
          {
            id: 'password',
            labelKey: 'integrations.types.shipping.sourceTypes.webcrawl.fields.password.label',
            type: 'password',
            required: true,
            placeholderKey: 'integrations.types.shipping.sourceTypes.webcrawl.fields.password.placeholder',
            descriptionKey: 'integrations.types.shipping.sourceTypes.webcrawl.fields.password.description'
          },
          {
            id: 'crawlInstructions',
            labelKey: 'integrations.types.shipping.sourceTypes.webcrawl.fields.crawlInstructions.label',
            type: 'textarea',
            required: true,
            placeholderKey: 'integrations.types.shipping.sourceTypes.webcrawl.fields.crawlInstructions.placeholder',
            descriptionKey: 'integrations.types.shipping.sourceTypes.webcrawl.fields.crawlInstructions.description'
          }
        ],
        helpContent: {
          titleKey: 'integrations.types.shipping.sourceTypes.webcrawl.helpContent.title',
          itemsKeys: [
            'integrations.types.shipping.sourceTypes.webcrawl.helpContent.items.0',
            'integrations.types.shipping.sourceTypes.webcrawl.helpContent.items.1',
            'integrations.types.shipping.sourceTypes.webcrawl.helpContent.items.2',
            'integrations.types.shipping.sourceTypes.webcrawl.helpContent.items.3'
          ],
          examples: [
            {
              titleKey: 'integrations.types.shipping.sourceTypes.webcrawl.helpContent.examples.trackPackage.title',
              descriptionKey: 'integrations.types.shipping.sourceTypes.webcrawl.helpContent.examples.trackPackage.description'
            },
            {
              titleKey: 'integrations.types.shipping.sourceTypes.webcrawl.helpContent.examples.createShipment.title',
              descriptionKey: 'integrations.types.shipping.sourceTypes.webcrawl.helpContent.examples.createShipment.description'
            }
          ]
        }
      }
    ]
  },

  pricing: {
    id: 'pricing',
    nameKey: 'integrations.types.pricing.name',
    descriptionKey: 'integrations.types.pricing.description',
    icon: 'DollarSign',
    category: 'data-sources',
    sourceTypes: [
      {
        id: 'csv',
        nameKey: 'integrations.types.pricing.sourceTypes.csv.name',
        icon: 'FileSpreadsheet',
        descriptionKey: 'integrations.types.pricing.sourceTypes.csv.description',
        color: 'green',
        supportsActions: false,
        defaultActions: [
          {
            id: 'default-search-by-oe',
            name: 'Search by OE Number',
            description: 'Look up pricing data for a specific part by its OE / manufacturer number.',
            inputParameters: [
              { name: 'oe_number', type: 'string', required: true, description: 'The OE / manufacturer part number to search for' }
            ],
            outputFormat: { type: 'json' },
            instructions: 'Search the uploaded CSV price list for the row(s) where the OE number column matches the provided oe_number. Return all matching fields (price, currency, internal catalog number, supplier part number, description, unit) as a JSON object. If no match is found, return an empty object.',
            inputSchema: {
              type: 'object',
              properties: {
                oe_number: { type: 'string', description: 'The OE / manufacturer part number to search for' }
              },
              required: ['oe_number']
            },
            outputSchema: {
              type: 'object',
              properties: {
                // ── Hoisted fields from the selected row (direct dot-path access) ──
                oe_number:                  { type: 'string',  description: 'The matched OE number' },
                price:                      { type: 'number',  description: 'Unit price (from selected row)' },
                currency:                   { type: 'string',  description: 'Currency code, e.g. USD' },
                internal_catalog_number:    { type: 'string',  description: 'Internal catalog / part number' },
                supplier_part_number:       { type: 'string',  description: 'Supplier SKU or part number' },
                description:                { type: 'string',  description: 'Part description' },
                unit:                       { type: 'string',  description: 'Unit of measure' },
                catalog_name:               { type: 'string',  description: 'Catalog / supplier name' },
                // ── Meta ──────────────────────────────────────────────────────────
                count:                      { type: 'integer', description: 'Total number of matching rows found' },
                _not_found:                 { type: 'boolean', description: 'True when no matching row was found' },
                _multi_match:               { type: 'boolean', description: 'True when more than one row matched' },
                _selected_behavior:         { type: 'string',  description: 'The multi_match_behavior that was applied' },
                // ── Full list (available when multi_match_behavior = "all") ──────
                results:                    { type: 'array',   description: 'All matching rows (use when multi_match_behavior = all)' },
              }
            }
          }
        ],
        fields: [
          {
            id: 'file',
            labelKey: 'integrations.types.pricing.sourceTypes.csv.fields.file.label',
            type: 'file',
            required: true,
            descriptionKey: 'integrations.types.pricing.sourceTypes.csv.fields.file.description',
            validation: {
              fileTypes: ['.csv', '.xlsx', '.xls']
            }
          },
          {
            id: 'catalog_name_column',
            labelKey: 'integrations.types.pricing.sourceTypes.csv.fields.catalogNameColumn.label',
            type: 'text',
            required: false,
            placeholderKey: 'integrations.types.pricing.sourceTypes.csv.fields.catalogNameColumn.placeholder',
            descriptionKey: 'integrations.types.pricing.sourceTypes.csv.fields.catalogNameColumn.description'
          },
          {
            id: 'oe_column',
            labelKey: 'integrations.types.pricing.sourceTypes.csv.fields.oeColumn.label',
            type: 'text',
            required: true,
            placeholderKey: 'integrations.types.pricing.sourceTypes.csv.fields.oeColumn.placeholder',
            descriptionKey: 'integrations.types.pricing.sourceTypes.csv.fields.oeColumn.description'
          },
          {
            id: 'price_column',
            labelKey: 'integrations.types.pricing.sourceTypes.csv.fields.priceColumn.label',
            type: 'text',
            required: true,
            placeholderKey: 'integrations.types.pricing.sourceTypes.csv.fields.priceColumn.placeholder',
            descriptionKey: 'integrations.types.pricing.sourceTypes.csv.fields.priceColumn.description'
          },
          {
            id: 'currency_column',
            labelKey: 'integrations.types.pricing.sourceTypes.csv.fields.currencyColumn.label',
            type: 'text',
            required: false,
            placeholderKey: 'integrations.types.pricing.sourceTypes.csv.fields.currencyColumn.placeholder',
            descriptionKey: 'integrations.types.pricing.sourceTypes.csv.fields.currencyColumn.description'
          },
          {
            id: 'internal_catalog_number_column',
            labelKey: 'integrations.types.pricing.sourceTypes.csv.fields.internalCatalogNumberColumn.label',
            type: 'text',
            required: false,
            placeholderKey: 'integrations.types.pricing.sourceTypes.csv.fields.internalCatalogNumberColumn.placeholder',
            descriptionKey: 'integrations.types.pricing.sourceTypes.csv.fields.internalCatalogNumberColumn.description'
          },
          {
            id: 'supplier_part_number_column',
            labelKey: 'integrations.types.pricing.sourceTypes.csv.fields.supplierPartNumberColumn.label',
            type: 'text',
            required: false,
            placeholderKey: 'integrations.types.pricing.sourceTypes.csv.fields.supplierPartNumberColumn.placeholder',
            descriptionKey: 'integrations.types.pricing.sourceTypes.csv.fields.supplierPartNumberColumn.description'
          },
          {
            id: 'description_column',
            labelKey: 'integrations.types.pricing.sourceTypes.csv.fields.descriptionColumn.label',
            type: 'text',
            required: false,
            placeholderKey: 'integrations.types.pricing.sourceTypes.csv.fields.descriptionColumn.placeholder',
            descriptionKey: 'integrations.types.pricing.sourceTypes.csv.fields.descriptionColumn.description'
          },
          {
            id: 'unit_column',
            labelKey: 'integrations.types.pricing.sourceTypes.csv.fields.unitColumn.label',
            type: 'text',
            required: false,
            placeholderKey: 'integrations.types.pricing.sourceTypes.csv.fields.unitColumn.placeholder',
            descriptionKey: 'integrations.types.pricing.sourceTypes.csv.fields.unitColumn.description'
          },
          {
            id: 'multi_match_behavior',
            labelKey: 'integrations.types.pricing.sourceTypes.csv.fields.multiMatchBehavior.label',
            type: 'select',
            required: false,
            descriptionKey: 'integrations.types.pricing.sourceTypes.csv.fields.multiMatchBehavior.description',
            options: [
              { value: 'first',           label: 'Use first match' },
              { value: 'cheapest',        label: 'Use cheapest price' },
              { value: 'most_expensive',  label: 'Use most expensive price' },
              { value: 'all',             label: 'Return all matches (as list)' },
            ]
          },
          {
            id: 'ignore_special_chars',
            labelKey: 'integrations.types.pricing.sourceTypes.csv.fields.ignoreSpecialChars.label',
            type: 'checkbox',
            required: false,
            descriptionKey: 'integrations.types.pricing.sourceTypes.csv.fields.ignoreSpecialChars.description'
          }
        ],
        helpContent: {
          titleKey: 'integrations.types.pricing.sourceTypes.csv.helpContent.title',
          itemsKeys: [
            'integrations.types.pricing.sourceTypes.csv.helpContent.items.0',
            'integrations.types.pricing.sourceTypes.csv.helpContent.items.1',
            'integrations.types.pricing.sourceTypes.csv.helpContent.items.2',
            'integrations.types.pricing.sourceTypes.csv.helpContent.items.3'
          ],
          examples: [
            {
              titleKey: 'integrations.types.pricing.sourceTypes.csv.helpContent.examples.uploadPriceList.title',
              descriptionKey: 'integrations.types.pricing.sourceTypes.csv.helpContent.examples.uploadPriceList.description'
            }
          ]
        }
      },
      {
        id: 'api',
        nameKey: 'integrations.types.pricing.sourceTypes.api.name',
        icon: 'Plug',
        descriptionKey: 'integrations.types.pricing.sourceTypes.api.description',
        color: 'blue',
        supportsActions: true,
        fields: [
          {
            id: 'baseUrl',
            labelKey: 'integrations.types.pricing.sourceTypes.api.fields.baseUrl.label',
            type: 'text',
            required: true,
            placeholderKey: 'integrations.types.pricing.sourceTypes.api.fields.baseUrl.placeholder',
            descriptionKey: 'integrations.types.pricing.sourceTypes.api.fields.baseUrl.description'
          },
          {
            id: 'authType',
            labelKey: 'integrations.types.pricing.sourceTypes.api.fields.authType.label',
            type: 'select',
            required: true,
            options: [
              { value: 'api_key', label: 'API Key' },
              { value: 'bearer', label: 'Bearer Token' },
              { value: 'basic', label: 'Basic Auth' },
              { value: 'none', label: 'No Auth' }
            ]
          },
          {
            id: 'apiKey',
            labelKey: 'integrations.types.pricing.sourceTypes.api.fields.apiKey.label',
            type: 'password',
            required: false,
            placeholderKey: 'integrations.types.pricing.sourceTypes.api.fields.apiKey.placeholder',
            descriptionKey: 'integrations.types.pricing.sourceTypes.api.fields.apiKey.description'
          },
          {
            id: 'customHeaders',
            labelKey: 'integrations.types.pricing.sourceTypes.api.fields.customHeaders.label',
            type: 'json',
            required: false,
            placeholderKey: 'integrations.types.pricing.sourceTypes.api.fields.customHeaders.placeholder',
            descriptionKey: 'integrations.types.pricing.sourceTypes.api.fields.customHeaders.description'
          }
        ],
        helpContent: {
          titleKey: 'integrations.types.pricing.sourceTypes.api.helpContent.title',
          itemsKeys: [
            'integrations.types.pricing.sourceTypes.api.helpContent.items.0',
            'integrations.types.pricing.sourceTypes.api.helpContent.items.1',
            'integrations.types.pricing.sourceTypes.api.helpContent.items.2',
            'integrations.types.pricing.sourceTypes.api.helpContent.items.3'
          ],
          examples: [
            {
              titleKey: 'integrations.types.pricing.sourceTypes.api.helpContent.examples.getPricing.title',
              descriptionKey: 'integrations.types.pricing.sourceTypes.api.helpContent.examples.getPricing.description'
            },
            {
              titleKey: 'integrations.types.pricing.sourceTypes.api.helpContent.examples.getPriceByPart.title',
              descriptionKey: 'integrations.types.pricing.sourceTypes.api.helpContent.examples.getPriceByPart.description'
            }
          ]
        }
      },
      {
        id: 'webcrawl',
        nameKey: 'integrations.types.pricing.sourceTypes.webcrawl.name',
        icon: 'Sparkles',
        descriptionKey: 'integrations.types.pricing.sourceTypes.webcrawl.description',
        color: 'purple',
        supportsActions: true,
        fields: [
          {
            id: 'websiteUrl',
            labelKey: 'integrations.types.pricing.sourceTypes.webcrawl.fields.websiteUrl.label',
            type: 'text',
            required: true,
            placeholderKey: 'integrations.types.pricing.sourceTypes.webcrawl.fields.websiteUrl.placeholder',
            descriptionKey: 'integrations.types.pricing.sourceTypes.webcrawl.fields.websiteUrl.description'
          },
          {
            id: 'username',
            labelKey: 'integrations.types.pricing.sourceTypes.webcrawl.fields.username.label',
            type: 'text',
            required: false,
            placeholderKey: 'integrations.types.pricing.sourceTypes.webcrawl.fields.username.placeholder',
            descriptionKey: 'integrations.types.pricing.sourceTypes.webcrawl.fields.username.description'
          },
          {
            id: 'password',
            labelKey: 'integrations.types.pricing.sourceTypes.webcrawl.fields.password.label',
            type: 'password',
            required: false,
            placeholderKey: 'integrations.types.pricing.sourceTypes.webcrawl.fields.password.placeholder',
            descriptionKey: 'integrations.types.pricing.sourceTypes.webcrawl.fields.password.description'
          },
          {
            id: 'crawlInstructions',
            labelKey: 'integrations.types.pricing.sourceTypes.webcrawl.fields.crawlInstructions.label',
            type: 'textarea',
            required: false,
            placeholderKey: 'integrations.types.pricing.sourceTypes.webcrawl.fields.crawlInstructions.placeholder',
            descriptionKey: 'integrations.types.pricing.sourceTypes.webcrawl.fields.crawlInstructions.description'
          }
        ],
        helpContent: {
          titleKey: 'integrations.types.pricing.sourceTypes.webcrawl.helpContent.title',
          itemsKeys: [
            'integrations.types.pricing.sourceTypes.webcrawl.helpContent.items.0',
            'integrations.types.pricing.sourceTypes.webcrawl.helpContent.items.1',
            'integrations.types.pricing.sourceTypes.webcrawl.helpContent.items.2',
            'integrations.types.pricing.sourceTypes.webcrawl.helpContent.items.3',
            'integrations.types.pricing.sourceTypes.webcrawl.helpContent.items.4'
          ],
          examples: [
            {
              titleKey: 'integrations.types.pricing.sourceTypes.webcrawl.helpContent.examples.getPriceByPart.title',
              descriptionKey: 'integrations.types.pricing.sourceTypes.webcrawl.helpContent.examples.getPriceByPart.description'
            },
            {
              titleKey: 'integrations.types.pricing.sourceTypes.webcrawl.helpContent.examples.comparePrices.title',
              descriptionKey: 'integrations.types.pricing.sourceTypes.webcrawl.helpContent.examples.comparePrices.description'
            }
          ]
        }
      }
    ]
  },

  catalog: {
    id: 'catalog',
    nameKey: 'integrations.types.catalog.name',
    descriptionKey: 'integrations.types.catalog.description',
    icon: 'BookOpen',
    category: 'data-sources',
    sourceTypes: [
      {
        id: 'api',
        nameKey: 'integrations.types.catalog.sourceTypes.api.name',
        icon: 'Plug',
        descriptionKey: 'integrations.types.catalog.sourceTypes.api.description',
        color: 'blue',
        supportsActions: true,
        fields: [
          {
            id: 'baseUrl',
            labelKey: 'integrations.types.catalog.sourceTypes.api.fields.baseUrl.label',
            type: 'text',
            required: true,
            placeholderKey: 'integrations.types.catalog.sourceTypes.api.fields.baseUrl.placeholder',
            descriptionKey: 'integrations.types.catalog.sourceTypes.api.fields.baseUrl.description'
          },
          {
            id: 'authType',
            labelKey: 'integrations.types.catalog.sourceTypes.api.fields.authType.label',
            type: 'select',
            required: true,
            options: [
              { value: 'api_key', label: 'API Key' },
              { value: 'bearer', label: 'Bearer Token' },
              { value: 'basic', label: 'Basic Auth' },
              { value: 'none', label: 'No Auth' }
            ]
          },
          {
            id: 'apiKey',
            labelKey: 'integrations.types.catalog.sourceTypes.api.fields.apiKey.label',
            type: 'password',
            required: false,
            placeholderKey: 'integrations.types.catalog.sourceTypes.api.fields.apiKey.placeholder',
            descriptionKey: 'integrations.types.catalog.sourceTypes.api.fields.apiKey.description'
          },
          {
            id: 'customHeaders',
            labelKey: 'integrations.types.catalog.sourceTypes.api.fields.customHeaders.label',
            type: 'json',
            required: false,
            placeholderKey: 'integrations.types.catalog.sourceTypes.api.fields.customHeaders.placeholder',
            descriptionKey: 'integrations.types.catalog.sourceTypes.api.fields.customHeaders.description'
          }
        ],
        helpContent: {
          titleKey: 'integrations.types.catalog.sourceTypes.api.helpContent.title',
          itemsKeys: [
            'integrations.types.catalog.sourceTypes.api.helpContent.items.0',
            'integrations.types.catalog.sourceTypes.api.helpContent.items.1',
            'integrations.types.catalog.sourceTypes.api.helpContent.items.2',
            'integrations.types.catalog.sourceTypes.api.helpContent.items.3'
          ],
          examples: [
            {
              titleKey: 'integrations.types.catalog.sourceTypes.api.helpContent.examples.lookupPart.title',
              descriptionKey: 'integrations.types.catalog.sourceTypes.api.helpContent.examples.lookupPart.description'
            },
            {
              titleKey: 'integrations.types.catalog.sourceTypes.api.helpContent.examples.searchByVIN.title',
              descriptionKey: 'integrations.types.catalog.sourceTypes.api.helpContent.examples.searchByVIN.description'
            }
          ]
        }
      },
      {
        id: 'webcrawl',
        nameKey: 'integrations.types.catalog.sourceTypes.webcrawl.name',
        icon: 'Sparkles',
        descriptionKey: 'integrations.types.catalog.sourceTypes.webcrawl.description',
        color: 'purple',
        supportsActions: true,
        fields: [
          {
            id: 'websiteUrl',
            labelKey: 'integrations.types.catalog.sourceTypes.webcrawl.fields.websiteUrl.label',
            type: 'text',
            required: true,
            placeholderKey: 'integrations.types.catalog.sourceTypes.webcrawl.fields.websiteUrl.placeholder',
            descriptionKey: 'integrations.types.catalog.sourceTypes.webcrawl.fields.websiteUrl.description'
          },
          {
            id: 'username',
            labelKey: 'integrations.types.catalog.sourceTypes.webcrawl.fields.username.label',
            type: 'text',
            required: true,
            placeholderKey: 'integrations.types.catalog.sourceTypes.webcrawl.fields.username.placeholder',
            descriptionKey: 'integrations.types.catalog.sourceTypes.webcrawl.fields.username.description'
          },
          {
            id: 'password',
            labelKey: 'integrations.types.catalog.sourceTypes.webcrawl.fields.password.label',
            type: 'password',
            required: true,
            placeholderKey: 'integrations.types.catalog.sourceTypes.webcrawl.fields.password.placeholder',
            descriptionKey: 'integrations.types.catalog.sourceTypes.webcrawl.fields.password.description'
          },
          {
            id: 'crawlInstructions',
            labelKey: 'integrations.types.catalog.sourceTypes.webcrawl.fields.crawlInstructions.label',
            type: 'textarea',
            required: false,
            placeholderKey: 'integrations.types.catalog.sourceTypes.webcrawl.fields.crawlInstructions.placeholder',
            descriptionKey: 'integrations.types.catalog.sourceTypes.webcrawl.fields.crawlInstructions.description'
          }
        ],
        helpContent: {
          titleKey: 'integrations.types.catalog.sourceTypes.webcrawl.helpContent.title',
          itemsKeys: [
            'integrations.types.catalog.sourceTypes.webcrawl.helpContent.items.0',
            'integrations.types.catalog.sourceTypes.webcrawl.helpContent.items.1',
            'integrations.types.catalog.sourceTypes.webcrawl.helpContent.items.2',
            'integrations.types.catalog.sourceTypes.webcrawl.helpContent.items.3',
            'integrations.types.catalog.sourceTypes.webcrawl.helpContent.items.4'
          ],
          examples: [
            {
              titleKey: 'integrations.types.catalog.sourceTypes.webcrawl.helpContent.examples.lookupPart.title',
              descriptionKey: 'integrations.types.catalog.sourceTypes.webcrawl.helpContent.examples.lookupPart.description'
            },
            {
              titleKey: 'integrations.types.catalog.sourceTypes.webcrawl.helpContent.examples.searchByVIN.title',
              descriptionKey: 'integrations.types.catalog.sourceTypes.webcrawl.helpContent.examples.searchByVIN.description'
            },
            {
              titleKey: 'integrations.types.catalog.sourceTypes.webcrawl.helpContent.examples.getPartDetails.title',
              descriptionKey: 'integrations.types.catalog.sourceTypes.webcrawl.helpContent.examples.getPartDetails.description'
            }
          ]
        }
      }
    ]
  }
};

// ---------------------------------------------------------------------------
// Supplier — dedicated integration type for per-part async supplier pricing
// Registered separately from "pricing" so workflows can target it explicitly.
// ---------------------------------------------------------------------------
INTEGRATIONS['supplier'] = {
  id: 'supplier',
  nameKey: 'integrations.types.supplier.name',
  descriptionKey: 'integrations.types.supplier.description',
  icon: 'Truck',
  category: 'data-sources',
  sourceTypes: [
    // ── CSV / Excel price-list ─────────────────────────────────────────────
    {
      id: 'csv',
      nameKey: 'integrations.types.supplier.sourceTypes.csv.name',
      icon: 'FileSpreadsheet',
      descriptionKey: 'integrations.types.supplier.sourceTypes.csv.description',
      color: 'green',
      supportsActions: true,
      // Action templates define the "what does this data do" layer.
      // Users pick a template, name their action, then map CSV columns to each slot.
      actionTemplates: [
        {
          id: 'lookup_supplier_price',
          name: 'Look Up Supplier Price',
          description: 'Given an OE part number, return price and availability from the CSV.',
          inputSlots: [],
          inputSlotsEditable: true,
          outputSlots: [
            { id: 'price',                   label: 'Price Column',                    required: true },
            { id: 'currency',                label: 'Currency Column',                 required: false },
            { id: 'availability',            label: 'Availability Column',             required: false },
            { id: 'supplier_part_number',    label: 'Supplier Part Number Column',     required: false },
            { id: 'internal_catalog_number', label: 'Internal Catalog Number Column',  required: false },
            { id: 'description',             label: 'Description Column',              required: false },
            { id: 'lead_time_days',          label: 'Lead Time (Days) Column',         required: false },
          ],
          outputSlotsEditable: false,
        },
        {
          id: 'general_lookup',
          name: 'General Lookup',
          description: 'Look up any key and return one or more output columns. Fully configurable.',
          inputSlots: [],
          inputSlotsEditable: true,
          outputSlots: [],
          outputSlotsEditable: true,
        },
      ],
      fields: [
        {
          id: 'file',
          labelKey: 'integrations.types.supplier.sourceTypes.csv.fields.file.label',
          type: 'file',
          required: true,
          descriptionKey: 'integrations.types.supplier.sourceTypes.csv.fields.file.description',
          validation: { fileTypes: ['.csv', '.xlsx', '.xls'] }
        },
        {
          id: 'supplier_name',
          labelKey: 'integrations.types.supplier.sourceTypes.csv.fields.supplierName.label',
          type: 'text',
          required: false,
          placeholderKey: 'integrations.types.supplier.sourceTypes.csv.fields.supplierName.placeholder',
          descriptionKey: 'integrations.types.supplier.sourceTypes.csv.fields.supplierName.description'
        },
        {
          id: 'ignore_special_chars',
          labelKey: 'integrations.types.supplier.sourceTypes.csv.fields.ignoreSpecialChars.label',
          type: 'checkbox',
          required: false,
          descriptionKey: 'integrations.types.supplier.sourceTypes.csv.fields.ignoreSpecialChars.description'
        },
      ],
      helpContent: {
        titleKey: 'integrations.types.supplier.sourceTypes.csv.helpContent.title',
        itemsKeys: [
          'integrations.types.supplier.sourceTypes.csv.helpContent.items.0',
          'integrations.types.supplier.sourceTypes.csv.helpContent.items.1',
          'integrations.types.supplier.sourceTypes.csv.helpContent.items.2',
          'integrations.types.supplier.sourceTypes.csv.helpContent.items.3',
        ],
        examples: [
          {
            titleKey: 'integrations.types.supplier.sourceTypes.csv.helpContent.examples.lookupPrice.title',
            descriptionKey: 'integrations.types.supplier.sourceTypes.csv.helpContent.examples.lookupPrice.description'
          }
        ]
      }
    },

    // ── REST API ──────────────────────────────────────────────────────────
    {
      id: 'api',
      nameKey: 'integrations.types.supplier.sourceTypes.api.name',
      icon: 'Plug',
      descriptionKey: 'integrations.types.supplier.sourceTypes.api.description',
      color: 'blue',
      supportsActions: true,
      fields: [
        {
          id: 'baseUrl',
          labelKey: 'integrations.types.supplier.sourceTypes.api.fields.baseUrl.label',
          type: 'text',
          required: true,
          placeholderKey: 'integrations.types.supplier.sourceTypes.api.fields.baseUrl.placeholder',
          descriptionKey: 'integrations.types.supplier.sourceTypes.api.fields.baseUrl.description'
        },
        {
          id: 'authType',
          labelKey: 'integrations.types.supplier.sourceTypes.api.fields.authType.label',
          type: 'select',
          required: true,
          options: [
            { value: 'api_key', label: 'API Key' },
            { value: 'bearer', label: 'Bearer Token' },
            { value: 'basic', label: 'Basic Auth' },
            { value: 'none', label: 'No Auth' }
          ]
        },
        {
          id: 'apiKey',
          labelKey: 'integrations.types.supplier.sourceTypes.api.fields.apiKey.label',
          type: 'password',
          required: false,
          placeholderKey: 'integrations.types.supplier.sourceTypes.api.fields.apiKey.placeholder',
          descriptionKey: 'integrations.types.supplier.sourceTypes.api.fields.apiKey.description'
        },
        {
          id: 'customHeaders',
          labelKey: 'integrations.types.supplier.sourceTypes.api.fields.customHeaders.label',
          type: 'json',
          required: false,
          placeholderKey: 'integrations.types.supplier.sourceTypes.api.fields.customHeaders.placeholder',
          descriptionKey: 'integrations.types.supplier.sourceTypes.api.fields.customHeaders.description'
        }
      ],
      helpContent: {
        titleKey: 'integrations.types.supplier.sourceTypes.api.helpContent.title',
        itemsKeys: [
          'integrations.types.supplier.sourceTypes.api.helpContent.items.0',
          'integrations.types.supplier.sourceTypes.api.helpContent.items.1',
          'integrations.types.supplier.sourceTypes.api.helpContent.items.2',
          'integrations.types.supplier.sourceTypes.api.helpContent.items.3',
        ],
        examples: [
          {
            titleKey: 'integrations.types.supplier.sourceTypes.api.helpContent.examples.getPrice.title',
            descriptionKey: 'integrations.types.supplier.sourceTypes.api.helpContent.examples.getPrice.description'
          }
        ]
      }
    },

    // ── Webcrawl / Cata Magic ──────────────────────────────────────────────
    {
      id: 'webcrawl',
      nameKey: 'integrations.types.supplier.sourceTypes.webcrawl.name',
      icon: 'Sparkles',
      descriptionKey: 'integrations.types.supplier.sourceTypes.webcrawl.description',
      color: 'purple',
      supportsActions: true,
      fields: [
        {
          id: 'websiteUrl',
          labelKey: 'integrations.types.supplier.sourceTypes.webcrawl.fields.websiteUrl.label',
          type: 'text',
          required: true,
          placeholderKey: 'integrations.types.supplier.sourceTypes.webcrawl.fields.websiteUrl.placeholder',
          descriptionKey: 'integrations.types.supplier.sourceTypes.webcrawl.fields.websiteUrl.description'
        },
        {
          id: 'username',
          labelKey: 'integrations.types.supplier.sourceTypes.webcrawl.fields.username.label',
          type: 'text',
          required: false,
          placeholderKey: 'integrations.types.supplier.sourceTypes.webcrawl.fields.username.placeholder',
          descriptionKey: 'integrations.types.supplier.sourceTypes.webcrawl.fields.username.description'
        },
        {
          id: 'password',
          labelKey: 'integrations.types.supplier.sourceTypes.webcrawl.fields.password.label',
          type: 'password',
          required: false,
          placeholderKey: 'integrations.types.supplier.sourceTypes.webcrawl.fields.password.placeholder',
          descriptionKey: 'integrations.types.supplier.sourceTypes.webcrawl.fields.password.description'
        },
        {
          id: 'crawlInstructions',
          labelKey: 'integrations.types.supplier.sourceTypes.webcrawl.fields.crawlInstructions.label',
          type: 'textarea',
          required: false,
          placeholderKey: 'integrations.types.supplier.sourceTypes.webcrawl.fields.crawlInstructions.placeholder',
          descriptionKey: 'integrations.types.supplier.sourceTypes.webcrawl.fields.crawlInstructions.description'
        }
      ],
      helpContent: {
        titleKey: 'integrations.types.supplier.sourceTypes.webcrawl.helpContent.title',
        itemsKeys: [
          'integrations.types.supplier.sourceTypes.webcrawl.helpContent.items.0',
          'integrations.types.supplier.sourceTypes.webcrawl.helpContent.items.1',
          'integrations.types.supplier.sourceTypes.webcrawl.helpContent.items.2',
          'integrations.types.supplier.sourceTypes.webcrawl.helpContent.items.3',
        ],
        examples: [
          {
            titleKey: 'integrations.types.supplier.sourceTypes.webcrawl.helpContent.examples.getPrice.title',
            descriptionKey: 'integrations.types.supplier.sourceTypes.webcrawl.helpContent.examples.getPrice.description'
          },
          {
            titleKey: 'integrations.types.supplier.sourceTypes.webcrawl.helpContent.examples.checkAvailability.title',
            descriptionKey: 'integrations.types.supplier.sourceTypes.webcrawl.helpContent.examples.checkAvailability.description'
          }
        ]
      }
    },

    // ── MCP (Model Context Protocol) ───────────────────────────────────────
    {
      id: 'mcp',
      nameKey: 'integrations.types.supplier.sourceTypes.mcp.name',
      icon: 'Server',
      descriptionKey: 'integrations.types.supplier.sourceTypes.mcp.description',
      color: 'blue',
      supportsActions: true,
      fields: [
        {
          id: 'mcpUrl',
          labelKey: 'integrations.types.supplier.sourceTypes.mcp.fields.mcpUrl.label',
          type: 'text',
          required: true,
          placeholderKey: 'integrations.types.supplier.sourceTypes.mcp.fields.mcpUrl.placeholder',
          descriptionKey: 'integrations.types.supplier.sourceTypes.mcp.fields.mcpUrl.description'
        },
        {
          id: 'authType',
          labelKey: 'integrations.types.supplier.sourceTypes.mcp.fields.authType.label',
          type: 'select',
          required: true,
          options: [
            { value: 'bearer', label: 'Bearer Token' },
            { value: 'api_key', label: 'API Key' },
            { value: 'basic', label: 'Basic Auth' },
            { value: 'none', label: 'No Auth' }
          ]
        },
        {
          id: 'mcpToken',
          labelKey: 'integrations.types.supplier.sourceTypes.mcp.fields.mcpToken.label',
          type: 'password',
          required: false,
          placeholderKey: 'integrations.types.supplier.sourceTypes.mcp.fields.mcpToken.placeholder',
          descriptionKey: 'integrations.types.supplier.sourceTypes.mcp.fields.mcpToken.description'
        },
        {
          id: 'toolArgs',
          labelKey: 'integrations.types.supplier.sourceTypes.mcp.fields.toolArgs.label',
          type: 'json',
          required: false,
          placeholderKey: 'integrations.types.supplier.sourceTypes.mcp.fields.toolArgs.placeholder',
          descriptionKey: 'integrations.types.supplier.sourceTypes.mcp.fields.toolArgs.description'
        },
        {
          id: 'customHeaders',
          labelKey: 'integrations.types.supplier.sourceTypes.mcp.fields.customHeaders.label',
          type: 'json',
          required: false,
          placeholderKey: 'integrations.types.supplier.sourceTypes.mcp.fields.customHeaders.placeholder',
          descriptionKey: 'integrations.types.supplier.sourceTypes.mcp.fields.customHeaders.description'
        }
      ],
      helpContent: {
        titleKey: 'integrations.types.supplier.sourceTypes.mcp.helpContent.title',
        itemsKeys: [
          'integrations.types.supplier.sourceTypes.mcp.helpContent.items.0',
          'integrations.types.supplier.sourceTypes.mcp.helpContent.items.1',
          'integrations.types.supplier.sourceTypes.mcp.helpContent.items.2',
          'integrations.types.supplier.sourceTypes.mcp.helpContent.items.3',
        ],
        examples: [
          {
            titleKey: 'integrations.types.supplier.sourceTypes.mcp.helpContent.examples.getPrice.title',
            descriptionKey: 'integrations.types.supplier.sourceTypes.mcp.helpContent.examples.getPrice.description'
          }
        ]
      }
    }
  ]
};

// Helper to get integration config by ID
export const getIntegrationConfig = (integrationId: string): IntegrationConfig | undefined => {
  return INTEGRATIONS[integrationId];
};

// Helper to get all integrations by category
export const getIntegrationsByCategory = (category: string): IntegrationConfig[] => {
  return Object.values(INTEGRATIONS).filter(i => i.category === category);
};
